import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import RaceDetailPage from '../components/raceDetail/RaceDetailPage';
import PredictionServiceNav from '../components/predictionService/PredictionServiceNav';
import {
  buildRaceDetailModel,
  buildRacePerformanceSummary,
} from '../components/raceDetail/utils';

const API_BASE = 'https://chiriri-keiba.com';
const PAGE_KEY = 'chiriri_model_race_list';
const RESULT_MARK_COLORS = {
  '◎': '#7bf1c8',
  '○': '#69d8ff',
  '▲': '#ffcf6e',
  '△': '#a8c8de',
};
const createEmptyData = () => ({
  tanpuku_df: [],
  umaren_df: [],
  sanrenpuku_df: [],
  sanrentan_df: [],
  race_name: '',
});

function ChiririModelRaceList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [raceList, setRaceList] = useState([]);
  const [selectedRace, setSelectedRace] = useState('');
  const [selectedRaceMeta, setSelectedRaceMeta] = useState(null);
  const [data, setData] = useState(createEmptyData);
  const [loading, setLoading] = useState(true);
  const [raceLoading, setRaceLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [roughnessDialogOpen, setRoughnessDialogOpen] = useState(false);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [resultsDate, setResultsDate] = useState('');
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsErrorMsg, setResultsErrorMsg] = useState('');
  const [resultSummaries, setResultSummaries] = useState([]);
  const [selectorExpanded, setSelectorExpanded] = useState(true);

  useEffect(() => {
    const fetchDates = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/chiriri_model/available_dates`, {
          params: { page: PAGE_KEY },
        });
        const dates = response.data?.dates ?? [];
        setAvailableDates(dates);
        if (dates.length > 0) {
          setSelectedDate(dates[0]);
          setResultsDate(dates[0]);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('日付一覧取得失敗:', error);
        setErrorMsg('日付一覧の取得に失敗しました。');
        setLoading(false);
      }
    };
    fetchDates();
  }, []);

  useEffect(() => {
    if (!selectedDate) return;

    const fetchRaceList = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE}/api/chiriri_model/race_list`, {
          params: { date: selectedDate, page: PAGE_KEY },
        });
        setRaceList(response.data?.race_list ?? []);
        setSelectedRace('');
        setSelectedRaceMeta(null);
        setData(createEmptyData());
        setErrorMsg('');
      } catch (error) {
        console.error('レース一覧取得失敗:', error);
        setErrorMsg('レース一覧の取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchRaceList();
  }, [selectedDate]);

  useEffect(() => {
    if (!resultsDialogOpen || !resultsDate) return;

    let cancelled = false;

    const fetchResultSummaries = async () => {
      setResultsLoading(true);
      setResultsErrorMsg('');
      try {
        const raceListResponse = await axios.get(`${API_BASE}/api/chiriri_model/race_list`, {
          params: { date: resultsDate, page: PAGE_KEY },
        });
        const resultRaceList = (raceListResponse.data?.race_list ?? []).filter(
          (race) => typeof race === 'string' || race?.has_prediction !== false
        );

        const summaries = await Promise.all(
          resultRaceList.map(async (race) => {
            const raceKey = typeof race === 'string' ? race : race?.race_key || race?.race_name;
            const raceName = typeof race === 'string' ? race : race?.race_name || raceKey;
            if (!raceKey && !raceName) return null;

            try {
              const detailResponse = await axios.get(`${API_BASE}/api/chiriri_model/predictions`, {
                params: {
                  race_info: raceKey || raceName,
                  date: resultsDate,
                  page: PAGE_KEY,
                },
              });
              return buildRacePerformanceSummary({
                data: {
                  tanpuku_df: detailResponse.data?.tanpuku_df ?? [],
                  umaren_df: detailResponse.data?.umaren_df ?? [],
                  sanrenpuku_df: detailResponse.data?.sanrenpuku_df ?? [],
                  sanrentan_df: detailResponse.data?.sanrentan_df ?? [],
                  race_name: detailResponse.data?.race_name ?? raceName ?? '',
                },
                selectedRaceMeta:
                  typeof race === 'string'
                    ? { race_name: raceName, race_key: raceKey }
                    : race,
                selectedDate: resultsDate,
              });
            } catch (detailError) {
              console.error('実績詳細取得失敗:', raceKey || raceName, detailError);
              return {
                raceKey: raceKey || raceName,
                raceName: raceName || raceKey,
                entries: [],
                hasConfirmedResult: false,
                error: true,
              };
            }
          })
        );

        if (!cancelled) {
          setResultSummaries(summaries.filter(Boolean));
        }
      } catch (error) {
        console.error('実績一覧取得失敗:', error);
        if (!cancelled) {
          setResultSummaries([]);
          setResultsErrorMsg('実績一覧の取得に失敗しました。');
        }
      } finally {
        if (!cancelled) {
          setResultsLoading(false);
        }
      }
    };

    fetchResultSummaries();

    return () => {
      cancelled = true;
    };
  }, [resultsDialogOpen, resultsDate]);

  const handleSelectRace = async (race) => {
    if (typeof race !== 'string' && race?.has_prediction === false) {
      setErrorMsg('このレースは表示対象ですが、予測ファイルが未生成のためまだ開けません。');
      return;
    }

    const raceKey = typeof race === 'string' ? race : race?.race_key || race?.race_name;
    const raceName = typeof race === 'string' ? race : race?.race_name || race?.race_key;
    const raceMeta = typeof race === 'string' ? { race_name: raceName, race_key: raceKey } : race;

    setSelectedRace(raceKey || raceName || '');
    setSelectedRaceMeta(raceMeta);
    setRaceLoading(true);
    setErrorMsg('');

    try {
      const response = await axios.get(`${API_BASE}/api/chiriri_model/predictions`, {
        params: { race_info: raceKey || raceName, date: selectedDate, page: PAGE_KEY },
      });
      setData({
        tanpuku_df: response.data?.tanpuku_df ?? [],
        umaren_df: response.data?.umaren_df ?? [],
        sanrenpuku_df: response.data?.sanrenpuku_df ?? [],
        sanrentan_df: response.data?.sanrentan_df ?? [],
        race_name: response.data?.race_name ?? raceName ?? '',
      });
      if (isMobile) {
        setSelectorExpanded(false);
      }
    } catch (error) {
      console.error('レース詳細取得失敗:', error);
      setData(createEmptyData());
      setErrorMsg('レース詳細の取得に失敗しました。');
    } finally {
      setRaceLoading(false);
    }
  };

  useEffect(() => {
    if (!isMobile) {
      setSelectorExpanded(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile && selectedDate) {
      setSelectorExpanded(true);
    }
  }, [isMobile, selectedDate]);

  const detailModel = useMemo(
    () => buildRaceDetailModel({ data, selectedRaceMeta, selectedDate }),
    [data, selectedRaceMeta, selectedDate]
  );

  const missingPredictionCount = useMemo(
    () =>
      raceList.filter((race) => typeof race !== 'string' && race?.has_prediction === false).length,
    [raceList]
  );

  const openResultsDialog = () => {
    setResultsDate(selectedDate || availableDates[0] || '');
    setResultsDialogOpen(true);
  };

  const renderMarkedCell = (entries) => {
    if (!entries?.length) {
      return <Typography sx={{ color: 'rgba(203, 229, 245, 0.58)' }}>-</Typography>;
    }

    return (
      <Stack spacing={0.8}>
        {entries.map((entry, index) => (
          <Box key={`${entry.mark}-${entry.horseNumber}-${index}`}>
            <Typography sx={{ fontWeight: 700, color: RESULT_MARK_COLORS[entry.mark] || '#eef8ff' }}>
              {entry.mark} {entry.horseNumber}番 {entry.horseName}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(203, 229, 245, 0.78)' }}>
              {entry.finishLabel} / 単勝 {entry.oddsLabel}
            </Typography>
          </Box>
        ))}
      </Stack>
    );
  };

  const renderPodiumCell = (entry) => {
    if (!entry) {
      return <Typography sx={{ color: 'rgba(203, 229, 245, 0.58)' }}>-</Typography>;
    }

    return (
      <Box>
        <Typography sx={{ fontWeight: 700 }}>
          {entry.horseNumber}番 {entry.horseName}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'rgba(203, 229, 245, 0.78)' }}>
          {entry.mark} / 単勝 {entry.oddsLabel}
        </Typography>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#050c16' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at 15% 0%, rgba(43,132,255,0.22), transparent 28%), radial-gradient(circle at 90% 10%, rgba(50,255,226,0.14), transparent 24%), linear-gradient(180deg, #02060d 0%, #07111d 46%, #03070f 100%)',
        color: '#eef8ff',
        fontFamily: '"Avenir Next", "Yu Gothic", "Hiragino Sans", sans-serif',
      }}
    >
      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 3, sm: 4 } }}>
        <Stack spacing={3}>
          <PredictionServiceNav activeKey="jra" />

          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              color: 'inherit',
              border: '1px solid rgba(92, 160, 219, 0.2)',
              background:
                'linear-gradient(180deg, rgba(10,24,41,0.92), rgba(4,10,20,0.95))',
              boxShadow: '0 24px 60px rgba(0, 0, 0, 0.28)',
            }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={2}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={{ xs: 0.5, md: 2 }}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: '#71dbff',
                        fontSize: 12,
                        letterSpacing: '0.24em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Race Selector
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: { xs: 24, sm: 30 }, fontWeight: 700 }}>
                      レース詳細を選択
                    </Typography>
                    {isMobile && selectedRaceMeta?.race_name && !selectorExpanded && (
                      <Typography sx={{ mt: 1, color: 'rgba(203, 229, 245, 0.72)', fontSize: 13 }}>
                        選択中: {selectedRaceMeta.race_name}
                      </Typography>
                    )}
                  </Box>

                  {!isMobile && (
                    <Button
                      variant="outlined"
                      onClick={openResultsDialog}
                      sx={{
                        mt: { md: 3 },
                        borderRadius: 999,
                        px: 2,
                        color: '#9ed7ff',
                        borderColor: 'rgba(92, 160, 219, 0.22)',
                        bgcolor: 'rgba(8, 18, 32, 0.72)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      実績はこちら
                    </Button>
                  )}
                </Stack>

                {isMobile ? (
                  <Button
                    variant="outlined"
                    onClick={() => setSelectorExpanded((prev) => !prev)}
                    endIcon={selectorExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                    sx={{
                      alignSelf: 'stretch',
                      borderRadius: 999,
                      color: '#9ed7ff',
                      borderColor: 'rgba(92, 160, 219, 0.22)',
                      bgcolor: 'rgba(8, 18, 32, 0.72)',
                    }}
                  >
                    {selectorExpanded ? '選択エリアを隠す' : '選択エリアを表示'}
                  </Button>
                ) : (
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 190,
                        '& .MuiOutlinedInput-root': {
                          color: '#ecf9ff',
                          bgcolor: 'rgba(7, 17, 31, 0.86)',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(92, 160, 219, 0.26)',
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#9ed7ff',
                        },
                      }}
                    >
                      <InputLabel id="date-select-label" sx={{ color: '#9ed7ff' }}>
                        日付選択
                      </InputLabel>
                      <Select
                        labelId="date-select-label"
                        value={selectedDate}
                        label="日付選択"
                        onChange={(event) => setSelectedDate(event.target.value)}
                      >
                        {availableDates.map((date) => (
                          <MenuItem key={date} value={date}>
                            {date}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <IconButton
                      onClick={() => setRoughnessDialogOpen(true)}
                      sx={{
                        color: '#9ed7ff',
                        border: '1px solid rgba(92, 160, 219, 0.22)',
                        bgcolor: 'rgba(8, 18, 32, 0.72)',
                      }}
                    >
                      <HelpOutlineIcon />
                    </IconButton>
                  </Stack>
                )}
              </Stack>

              <Collapse in={!isMobile || selectorExpanded} timeout="auto" unmountOnExit>
                <Stack spacing={2.5}>
                  {isMobile && (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Button
                        variant="outlined"
                        onClick={openResultsDialog}
                        sx={{
                          borderRadius: 999,
                          px: 2,
                          color: '#9ed7ff',
                          borderColor: 'rgba(92, 160, 219, 0.22)',
                          bgcolor: 'rgba(8, 18, 32, 0.72)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        実績はこちら
                      </Button>

                      <FormControl
                        size="small"
                        sx={{
                          minWidth: 190,
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            color: '#ecf9ff',
                            bgcolor: 'rgba(7, 17, 31, 0.86)',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(92, 160, 219, 0.26)',
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#9ed7ff',
                          },
                        }}
                      >
                        <InputLabel id="date-select-label-mobile" sx={{ color: '#9ed7ff' }}>
                          日付選択
                        </InputLabel>
                        <Select
                          labelId="date-select-label-mobile"
                          value={selectedDate}
                          label="日付選択"
                          onChange={(event) => setSelectedDate(event.target.value)}
                        >
                          {availableDates.map((date) => (
                            <MenuItem key={date} value={date}>
                              {date}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <IconButton
                        onClick={() => setRoughnessDialogOpen(true)}
                        sx={{
                          color: '#9ed7ff',
                          border: '1px solid rgba(92, 160, 219, 0.22)',
                          bgcolor: 'rgba(8, 18, 32, 0.72)',
                        }}
                      >
                        <HelpOutlineIcon />
                      </IconButton>
                    </Stack>
                  )}

                  <Alert
                    severity="info"
                    sx={{
                      color: '#eaf8ff',
                      bgcolor: alpha('#2a7dff', 0.12),
                      border: '1px solid rgba(100, 171, 255, 0.2)',
                    }}
                  >
                    土日の予想はレース当日9時目安に公開されます。
                  </Alert>

                  {missingPredictionCount > 0 && (
                    <Alert
                      severity="warning"
                      sx={{
                        color: '#fff5d6',
                        bgcolor: alpha('#ffb347', 0.12),
                        border: '1px solid rgba(255, 187, 86, 0.2)',
                      }}
                    >
                      表示対象レースのうち {missingPredictionCount} レースは予測未生成のため、選択を無効化しています。
                    </Alert>
                  )}

                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1.25,
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(auto-fit, minmax(220px, 1fr))',
                      },
                    }}
                  >
                    {raceList.map((race) => {
                      const raceName = typeof race === 'string' ? race : race?.race_name;
                      const raceKey = typeof race === 'string' ? race : race?.race_key || race?.race_name;
                      const roughness = typeof race === 'string' ? null : race?.roughness_raw || race?.roughness;
                      const confidence = typeof race === 'string' ? null : race?.confidence_raw || race?.confidence;
                      const surfaceLabel = typeof race === 'string' ? null : race?.surface_label;
                      const startTime = typeof race === 'string' ? null : race?.start_time;
                      const hasPrediction = typeof race === 'string' ? true : race?.has_prediction !== false;
                      const isSelected = selectedRace === (raceKey || raceName);
                      if (!raceName) return null;

                      return (
                        <Button
                          key={raceKey || raceName}
                          disabled={!hasPrediction}
                          onClick={() => handleSelectRace(race)}
                          sx={{
                            width: '100%',
                            minWidth: 0,
                            px: 2,
                            py: 1.25,
                            borderRadius: 3,
                            textTransform: 'none',
                            alignItems: 'flex-start',
                            border: '1px solid',
                            borderColor: isSelected
                              ? 'rgba(108, 247, 255, 0.42)'
                              : 'rgba(92, 160, 219, 0.18)',
                            background: isSelected
                              ? 'linear-gradient(135deg, rgba(22,111,199,0.94), rgba(14,54,109,0.94))'
                              : 'linear-gradient(180deg, rgba(9,20,35,0.88), rgba(6,14,25,0.88))',
                            color: '#eef8ff',
                            boxShadow: isSelected ? '0 0 22px rgba(76, 186, 255, 0.18)' : 'none',
                            '&.Mui-disabled': {
                              color: 'rgba(195, 216, 229, 0.32)',
                              borderColor: 'rgba(92, 160, 219, 0.08)',
                            },
                          }}
                        >
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography sx={{ fontWeight: 700 }}>{raceName}</Typography>
                            <Typography sx={{ mt: 0.5, fontSize: 12, color: 'rgba(200, 228, 245, 0.68)' }}>
                              {[
                                startTime ? `発走 ${startTime}` : null,
                                surfaceLabel,
                                roughness ? `荒れ度 ${roughness}` : null,
                                confidence ? `自信度 ${confidence}` : null,
                              ]
                                .filter(Boolean)
                                .join(' / ') || 'AIデータ待機中'}
                            </Typography>
                          </Box>
                        </Button>
                      );
                    })}
                  </Box>
                </Stack>
              </Collapse>
            </Stack>
          </Paper>

          {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

          {raceLoading && (
            <Alert severity="info" icon={<CircularProgress size={16} />}>
              レース詳細を読み込み中...
            </Alert>
          )}

          {detailModel ? (
            <RaceDetailPage model={detailModel} />
          ) : (
            <Paper
              sx={{
                p: { xs: 3, sm: 5 },
                borderRadius: 4,
                textAlign: 'center',
                color: '#eef8ff',
                border: '1px solid rgba(92, 160, 219, 0.18)',
                background:
                  'linear-gradient(180deg, rgba(10,24,41,0.84), rgba(4,10,20,0.9))',
              }}
            >
              <Typography
                sx={{
                  color: '#71dbff',
                  fontSize: 12,
                  letterSpacing: '0.24em',
                  textTransform: 'uppercase',
                }}
              >
                Analysis Standby
              </Typography>
              <Typography sx={{ mt: 1.5, fontSize: { xs: 22, sm: 28 }, fontWeight: 700 }}>
                レースを選択するとAI分析画面を表示します
              </Typography>
              <Typography sx={{ mt: 1, color: 'rgba(203, 229, 245, 0.72)' }}>
                勝率グラフ、注目馬、出走馬データ、最終予想をこの下にまとめて表示します。
              </Typography>
            </Paper>
          )}
        </Stack>
      </Box>

      <Dialog open={roughnessDialogOpen} onClose={() => setRoughnessDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>レースの荒れ度合について</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            荒れ度は、AI勝率上位3頭の単勝オッズ合計をもとに判定しています。予想確率は別指標として、画面上部のトップ勝率で確認できます。
          </Typography>
          <Typography variant="subtitle2" sx={{ color: '#7bf1c8', fontWeight: 700 }}>
            C
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            上位3頭オッズ合計が 12 以下。順当寄りで、上位人気に資金が集まっている状態です。
          </Typography>
          <Typography variant="subtitle2" sx={{ color: '#69d8ff', fontWeight: 700 }}>
            B
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            12 超 20 以下。標準的な難易度で、やや順当寄りです。
          </Typography>
          <Typography variant="subtitle2" sx={{ color: '#ffb347', fontWeight: 700 }}>
            A
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            20 超 30 以下。人気がやや割れていて、相手選びが難しくなるゾーンです。
          </Typography>
          <Typography variant="subtitle2" sx={{ color: '#d9534f', fontWeight: 700 }}>
            S
          </Typography>
          <Typography variant="body2">
            30 超。上位人気が割れていて、波乱寄りとみなすレースです。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoughnessDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resultsDialogOpen}
        onClose={() => setResultsDialogOpen(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            color: '#eef8ff',
            border: '1px solid rgba(92, 160, 219, 0.2)',
            background:
              'linear-gradient(180deg, rgba(10,24,41,0.96), rgba(4,10,20,0.98))',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>AI予想の実績一覧</DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(92, 160, 219, 0.14)' }}>
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography sx={{ color: 'rgba(203, 229, 245, 0.72)' }}>
                日付を選択すると、その日の各レースで ◎ / ○ / ▲ / △ の着順と、実際の1-3着を一覧表示します。
              </Typography>
              <FormControl
                size="small"
                sx={{
                  minWidth: 190,
                  '& .MuiOutlinedInput-root': {
                    color: '#ecf9ff',
                    bgcolor: 'rgba(7, 17, 31, 0.86)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(92, 160, 219, 0.26)',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#9ed7ff',
                  },
                }}
              >
                <InputLabel id="results-date-select-label" sx={{ color: '#9ed7ff' }}>
                  日付選択
                </InputLabel>
                <Select
                  labelId="results-date-select-label"
                  value={resultsDate}
                  label="日付選択"
                  onChange={(event) => setResultsDate(event.target.value)}
                >
                  {availableDates.map((date) => (
                    <MenuItem key={date} value={date}>
                      {date}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {resultsLoading && (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                実績一覧を読み込み中...
              </Alert>
            )}

            {resultsErrorMsg && <Alert severity="error">{resultsErrorMsg}</Alert>}

            {!resultsLoading && !resultsErrorMsg && resultSummaries.length === 0 && (
              <Alert severity="warning">この日付で表示できる実績データがありません。</Alert>
            )}

            {!resultsLoading && !resultsErrorMsg && resultSummaries.length > 0 && (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(92, 160, 219, 0.16)',
                  background:
                    'linear-gradient(180deg, rgba(12,31,52,0.88), rgba(7,18,33,0.9))',
                }}
              >
                <Table sx={{ minWidth: 1220 }}>
                  <TableHead>
                    <TableRow>
                      {[
                        'レース',
                        '結果',
                        '◎',
                        '○',
                        '▲',
                        '△',
                        '1着',
                        '2着',
                        '3着',
                        '馬連想定',
                        '3連複想定',
                        '3連単想定',
                      ].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: '#9ed7ff',
                            fontWeight: 700,
                            borderColor: 'rgba(92, 160, 219, 0.12)',
                            whiteSpace: 'nowrap',
                            bgcolor: 'rgba(8, 18, 32, 0.72)',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultSummaries.map((summary) => {
                      const marks = {
                        '◎': summary.entries.filter((entry) => entry.mark === '◎'),
                        '○': summary.entries.filter((entry) => entry.mark === '○'),
                        '▲': summary.entries.filter((entry) => entry.mark === '▲'),
                        '△': summary.entries.filter((entry) => entry.mark === '△'),
                      };
                      const podiumMap = new Map(
                        summary.podiumEntries.map((entry) => [entry.position, entry])
                      );

                      return (
                        <TableRow key={summary.raceKey} hover>
                          <TableCell
                            sx={{ color: '#eef8ff', borderColor: 'rgba(92, 160, 219, 0.1)', minWidth: 110 }}
                          >
                            <Typography sx={{ fontWeight: 700 }}>{summary.raceName}</Typography>
                          </TableCell>
                          <TableCell
                            sx={{ color: '#eef8ff', borderColor: 'rgba(92, 160, 219, 0.1)', minWidth: 92 }}
                          >
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: summary.hasConfirmedResult ? '#7fefff' : 'rgba(203, 229, 245, 0.62)',
                              }}
                            >
                              {summary.hasConfirmedResult ? '確定済み' : '未確定'}
                            </Typography>
                            {summary.error && (
                              <Typography sx={{ mt: 0.5, fontSize: 11, color: '#ffb3b3' }}>
                                取得失敗
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(92, 160, 219, 0.1)', minWidth: 180 }}>
                            {renderMarkedCell(marks['◎'])}
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(92, 160, 219, 0.1)', minWidth: 180 }}>
                            {renderMarkedCell(marks['○'])}
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(92, 160, 219, 0.1)', minWidth: 180 }}>
                            {renderMarkedCell(marks['▲'])}
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(92, 160, 219, 0.1)', minWidth: 220 }}>
                            {renderMarkedCell(marks['△'])}
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(92, 160, 219, 0.1)', minWidth: 180 }}>
                            {renderPodiumCell(podiumMap.get(1))}
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(92, 160, 219, 0.1)', minWidth: 180 }}>
                            {renderPodiumCell(podiumMap.get(2))}
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(92, 160, 219, 0.1)', minWidth: 180 }}>
                            {renderPodiumCell(podiumMap.get(3))}
                          </TableCell>
                          <TableCell
                            sx={{ color: '#eef8ff', borderColor: 'rgba(92, 160, 219, 0.1)', whiteSpace: 'nowrap' }}
                          >
                            {summary.resultOdds?.umaren || '-'}
                          </TableCell>
                          <TableCell
                            sx={{ color: '#eef8ff', borderColor: 'rgba(92, 160, 219, 0.1)', whiteSpace: 'nowrap' }}
                          >
                            {summary.resultOdds?.sanrenpuku || '-'}
                          </TableCell>
                          <TableCell
                            sx={{ color: '#eef8ff', borderColor: 'rgba(92, 160, 219, 0.1)', whiteSpace: 'nowrap' }}
                          >
                            {summary.resultOdds?.sanrentan || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(92, 160, 219, 0.14)' }}>
          <Button onClick={() => setResultsDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChiririModelRaceList;
